import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import { ResourceJsonInfoPanel } from '@/features/controller/configuration/components/ResourceJsonInfoPanel'
import { ControllerConfigurationFileProvider } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import { ControllerConfigurationBasePage } from '@/features/controller/configuration/pages/ControllerConfigurationBasePage'
import { ControllerConfigTypeId } from '@/features/controller/configuration/types'
import { ResourceJsonConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'
import { enrichBatteryResourceIdsInSchema } from '@/features/controller/configuration/utils/enrichBatteryResourceIdsInSchema'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { useControllerDescriptionQuery } from '@/features/controller/hooks/useControllerDescriptionQuery'
import { useResourcesByControllerEquipmentNumberQuery } from '@/features/resource/hooks/useResourcesByControllerEquipmentNumberQuery'

export function ControllerConfigurationResourceJsonPage() {
  const { controllerId } = useParams()
  const { t } = useTranslation()
  const { controllerDescription, isLoading: isControllerDescriptionLoading } = useControllerDescriptionQuery(
    controllerId!,
    { enabled: Boolean(controllerId) },
  )
  const {
    resources,
    isLoading: isResourcesLoading,
    isError: isResourcesError,
  } = useResourcesByControllerEquipmentNumberQuery(controllerDescription?.equipmentNumber)

  const schema = useMemo(() => enrichBatteryResourceIdsInSchema(resources.map((r) => r.resourceID)), [resources])

  if (!controllerId) {
    return null
  }

  // Wait for the controller description and the resources query before mounting the editor.
  // The schema is built once with its final shape so Monaco's process-wide json defaults are not
  // re-applied while the user is typing.
  if (isControllerDescriptionLoading || isResourcesLoading) {
    return <FullPageSpinner />
  }

  return (
    <ControllerConfigurationFileProvider configTypeId={ControllerConfigTypeId.ResourceJson} controllerId={controllerId}>
      <ControllerConfigurationBasePage
        activeTab={ControllerRouteInformation.CONTROLLER_CONFIG_RESOURCE_JSON.routePath}
        configType={ResourceJsonConfigType}
        controllerId={controllerId}
        infoPanel={<ResourceJsonInfoPanel isError={isResourcesError} resources={resources} />}
        pageTitle={t('controllers.configuration.resource_json.title')}
        schema={schema}
      />
    </ControllerConfigurationFileProvider>
  )
}
